/**
 * The vocabulary of customer-facing documents the system can email.
 *
 * Every document that can leave the system as an email attachment has exactly one
 * key here. Templates use these keys to declare which documents they apply to, and
 * (in the SES change) senders use them to declare which documents they send.
 *
 * The 15 accounting/service documents reuse `DocumentTypesKeys` verbatim rather
 * than redefining them, so the two vocabularies cannot drift. The report keys have
 * no equivalent there — reports are rendered straight from screen data and never
 * become `documents` rows.
 */
import { documentTypesEnum, type DocumentTypes, type DocumentTypesKeys } from '../vouchers/helpers';

/** Reports and automated mail — no `documents` row exists for these. */
export const REPORT_DOCUMENT_KEYS = [
    'REPORT_TRAVEL_FILE_DETAILS',
    'REPORT_TRAVEL_FILE_FULL_VIEW',
    'REPORT_PASSENGERS_LIST',
    'REPORT_ROOMING_LIST',
    'REPORT_HOTEL_LIST',
    'REPORT_ITINERARY',
    'REPORT_TOUR_PASSENGERS',
    'REPORT_TOUR_ACCOUNT_SUMMARY',
    'TOUR_REGISTRATION_RECEIPT'
] as const;

export type ReportDocumentKey = (typeof REPORT_DOCUMENT_KEYS)[number];

/** Every emailable document: the 15 accounting/service types plus 9 reports. */
export type EmailDocumentKey = DocumentTypesKeys | ReportDocumentKey;

/**
 * Labels for the settings UI. Accounting types reuse the existing display names so a
 * template's document list reads the same as the document itself does elsewhere.
 */
export const EMAIL_DOCUMENT_LABELS: Record<EmailDocumentKey, string> = {
    VOUCHER: documentTypesEnum.VOUCHER,
    REFUND: documentTypesEnum.REFUND,
    INVOICE: documentTypesEnum.INVOICE,
    TAX_INVOICE: documentTypesEnum.TAX_INVOICE,
    AGENT_INVOICE: documentTypesEnum.AGENT_INVOICE,
    RECEIPT: documentTypesEnum.RECEIPT,
    TRANSFER_RECEIPT: documentTypesEnum.TRANSFER_RECEIPT,
    CREDIT_DEBIT: documentTypesEnum.CREDIT_DEBIT,
    DEBIT_CREDIT: documentTypesEnum.DEBIT_CREDIT,
    REMITTANCE: documentTypesEnum.REMITTANCE,
    PRQ: documentTypesEnum.PRQ,
    STATEMENT: documentTypesEnum.STATEMENT,
    DEPOSIT: documentTypesEnum.DEPOSIT,
    WITHDRAWAL: documentTypesEnum.WITHDRAWAL,
    EXTENDED_REFUND: documentTypesEnum.EXTENDED_REFUND,

    REPORT_TRAVEL_FILE_DETAILS: 'Travel File Details Report',
    REPORT_TRAVEL_FILE_FULL_VIEW: 'Travel File Full View',
    REPORT_PASSENGERS_LIST: 'Passengers List Report',
    REPORT_ROOMING_LIST: 'Rooming List Report',
    REPORT_HOTEL_LIST: 'Hotel List Report',
    REPORT_ITINERARY: 'Itinerary Report',
    REPORT_TOUR_PASSENGERS: 'Tour Passenger List',
    REPORT_TOUR_ACCOUNT_SUMMARY: 'Tour Account Summary',
    TOUR_REGISTRATION_RECEIPT: 'Tour Registration Receipt (automated)'
};

/** Grouping for the settings UI, so 24 checkboxes aren't presented as one flat list. */
export const EMAIL_DOCUMENT_GROUPS: Array<{ label: string; keys: EmailDocumentKey[] }> = [
    {
        label: 'Accounting Documents',
        keys: [
            'INVOICE',
            'TAX_INVOICE',
            'AGENT_INVOICE',
            'RECEIPT',
            'TRANSFER_RECEIPT',
            'CREDIT_DEBIT',
            'DEBIT_CREDIT',
            'REMITTANCE',
            'PRQ',
            'STATEMENT',
            'DEPOSIT',
            'WITHDRAWAL'
        ]
    },
    { label: 'Service Documents', keys: ['VOUCHER', 'REFUND', 'EXTENDED_REFUND'] },
    {
        label: 'Reports',
        keys: [
            'REPORT_TRAVEL_FILE_DETAILS',
            'REPORT_TRAVEL_FILE_FULL_VIEW',
            'REPORT_PASSENGERS_LIST',
            'REPORT_ROOMING_LIST',
            'REPORT_HOTEL_LIST',
            'REPORT_ITINERARY',
            'REPORT_TOUR_PASSENGERS',
            'REPORT_TOUR_ACCOUNT_SUMMARY'
        ]
    },
    { label: 'Automated', keys: ['TOUR_REGISTRATION_RECEIPT'] }
];

/**
 * Document key → the permission that allows emailing that document.
 *
 * Written out in full rather than composed, so each key is greppable and the
 * catalog-coverage test can see it. Keys absent from this map have no email
 * permission of their own: reports are governed by their own `:export` right,
 * and Statement / Deposit / Withdrawal have no email gate in the catalog.
 */
export const EMAIL_PERMISSION_BY_DOCUMENT_KEY: Partial<Record<EmailDocumentKey, string>> = {
    VOUCHER: 'voucher:email',
    INVOICE: 'invoice:email',
    TAX_INVOICE: 'tax_invoice:email',
    AGENT_INVOICE: 'agent_invoice:email',
    RECEIPT: 'receipt:email',
    TRANSFER_RECEIPT: 'receipt:email',
    CREDIT_DEBIT: 'credit_debit_note:email',
    DEBIT_CREDIT: 'credit_debit_note:email',
    REMITTANCE: 'remittance:email',
    PRQ: 'prq:email',
    REFUND: 'refund:email',
    EXTENDED_REFUND: 'refund:email'
};

export const ALL_EMAIL_DOCUMENT_KEYS = Object.keys(EMAIL_DOCUMENT_LABELS) as EmailDocumentKey[];

/**
 * Map a stored document's display type back to its key.
 *
 * Documents persist their `type` as a display string ('Tax Invoice'), so the send
 * path has a label where it needs a key. Built by inverting `documentTypesEnum`
 * rather than hand-written, so a new document type cannot be added without also
 * appearing here.
 */
const TYPE_TO_KEY = Object.entries(documentTypesEnum).reduce<Record<string, DocumentTypesKeys>>(
    (acc, [key, label]) => {
        // 'Payment Order Remittance' and 'Payment Order (Remittance)' both exist in the
        // wild; the enum holds the parenthesised form, and the alias is handled below.
        acc[label] = key as DocumentTypesKeys;
        return acc;
    },
    {}
);
TYPE_TO_KEY['Payment Order Remittance'] = 'REMITTANCE';

export function documentTypeToKey(type: DocumentTypes | string | undefined | null): EmailDocumentKey | undefined {
    if (!type) return undefined;
    return TYPE_TO_KEY[type];
}
